
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import LessonNavigation from '@/components/lesson/LessonNavigation';
import LessonErrorDisplay from '@/components/lesson/LessonErrorDisplay';
import LessonProgramCheckIn from '@/components/lesson/LessonProgramCheckIn';
import LessonContent from '@/components/lesson/LessonContent';
import { getLessonByDay } from '@/utils/lessonUtils';
import { lessons as defaultLessons } from '@/data/lessons';
import { toast } from "sonner";
import { useUser } from '@/context/UserContext';
import analytics from '@/utils/analytics';
import errorMonitoring from '@/utils/errorMonitoring';

const Lesson = () => {
  const { day } = useParams<{ day: string }>();
  const dayNumber = parseInt(day || '1');
  const [currentLesson, setCurrentLesson] = useState(getLessonByDay(dayNumber) || defaultLessons[0]);
  const [completedDay, setCompletedDay] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showProgramCheckIn, setShowProgramCheckIn] = useState(false);
  const navigate = useNavigate();
  const { userProgress } = useUser();

  useEffect(() => {
    try {
      // Track page view
      analytics.trackPageView(`lesson_${dayNumber}`);
      
      const lesson = getLessonByDay(dayNumber);
      if (lesson) {
        setCurrentLesson(lesson);
        setError(null);
        
        // Track lesson view
        analytics.trackLessonEngagement(dayNumber, 'view');
        
        // Check if we should show program check-in (days 7, 30, or final day if completed but didn't do check-in)
        const shouldShowProgramCheckIn = 
          (dayNumber === 7 && !userProgress.completedDays.includes(7)) || 
          (dayNumber === 30 && !userProgress.completedDays.includes(30));
        
        setShowProgramCheckIn(shouldShowProgramCheckIn);
      } else {
        const errorMsg = `Could not load lesson for day ${dayNumber}`;
        setError(errorMsg);
        toast.error(`Error: ${errorMsg}`);
        errorMonitoring.reportError(new Error(errorMsg));
      }
      setCompletedDay(false);
      setEarnedPoints(0);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error loading lesson');
      console.error("Error loading lesson:", error);
      setError("Failed to load lesson content. Please try again later.");
      toast.error("Error loading lesson content");
      errorMonitoring.reportError(error);
    }
  }, [dayNumber, userProgress.completedDays]);

  // Check if this day is already completed
  const isDayCompleted = userProgress.completedDays.includes(dayNumber);
  
  // Check if this day is accessible to the user
  const isDayAccessible = dayNumber <= userProgress.currentDay;

  useEffect(() => {
    // If user tries to access a locked day, redirect them
    if (!isDayAccessible && dayNumber > userProgress.currentDay) {
      const errorMsg = "This lesson is locked. Please complete previous lessons first.";
      toast.error(errorMsg);
      analytics.trackUserAction('access_locked_content', { day: dayNumber });
      navigate(`/lesson/${userProgress.currentDay}`);
    }
  }, [dayNumber, isDayAccessible, navigate, userProgress.currentDay]);

  const goToNextDay = () => {
    const nextDay = dayNumber < 30 ? dayNumber + 1 : 30;
    analytics.trackUserAction('navigation', { from: dayNumber, to: nextDay, direction: 'next' });
    navigate(`/lesson/${nextDay}`);
  };

  // Check if this is day 30 and the user has completed the course
  const showFinalProgramCheckIn = dayNumber === 30 && isDayCompleted && !showProgramCheckIn;

  return (
    <Layout>
      <div className="container max-w-4xl py-12">
        <LessonNavigation dayNumber={dayNumber} />
        <LessonErrorDisplay error={error} />

        {showProgramCheckIn ? (
          <LessonProgramCheckIn
            dayNumber={dayNumber}
            showProgramCheckIn={showProgramCheckIn}
            setShowProgramCheckIn={setShowProgramCheckIn}
            setEarnedPoints={setEarnedPoints}
          />
        ) : (
          <LessonContent
            currentLesson={currentLesson}
            dayNumber={dayNumber}
            isDayCompleted={isDayCompleted}
            completedDay={completedDay}
            earnedPoints={earnedPoints}
            setEarnedPoints={setEarnedPoints}
            setCompletedDay={setCompletedDay}
            setError={setError}
            goToNextDay={goToNextDay}
            userProgressStreak={userProgress.streak}
            userProgressPoints={userProgress.points}
            showFinalProgramCheckIn={showFinalProgramCheckIn}
          />
        )}
      </div>
    </Layout>
  );
};

export default Lesson;
